import { SQLModule, SQLQuizQuestion } from './types';
import { UnifiedQuizQuestion } from '@/types/unifiedQuizTypes';

export const createComprehensiveSQLQuestions = (moduleId: string, moduleTitle: string): UnifiedQuizQuestion[] => {
  const questionSets: Record<string, UnifiedQuizQuestion[]> = {
    'sql-basics-1': [
      {
        id: 'sql-basics-1-q1',
        question: 'What does SQL stand for?',
        options: [
          'Structured Query Language',
          'Simple Query Language',
          'System Query Language',
          'Standard Query Language'
        ],
        correctAnswer: 0,
        difficulty: 'easy',
        explanation: 'SQL stands for Structured Query Language, the standard language for managing relational databases.',
        category: 'fundamentals',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'sql-basics-1-q2',
        question: 'Which statement is used to retrieve data from a database?',
        options: ['GET', 'FETCH', 'SELECT', 'RETRIEVE'],
        correctAnswer: 2,
        difficulty: 'easy',
        explanation: 'SELECT is the primary SQL statement used to retrieve data from database tables.',
        category: 'basic-queries',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'sql-basics-1-q3',
        question: 'What does the asterisk (*) represent in a SELECT statement?',
        options: [
          'A comment',
          'All columns',
          'A multiplication operator',
          'An error'
        ],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: 'The asterisk (*) in a SELECT statement means "select all columns" from the specified table.',
        category: 'basic-queries',
        points: 10,
        type: 'multiple-choice'
      }
    ],
    'sql-basics-2': [
      {
        id: 'sql-basics-2-q1',
        question: 'Which clause is used to filter records in SQL?',
        options: ['FILTER', 'WHERE', 'HAVING', 'IF'],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'The WHERE clause is used to filter records based on specified conditions.',
        category: 'filtering',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'sql-basics-2-q2',
        question: 'Which operator is used for pattern matching in SQL?',
        options: ['=', 'LIKE', 'MATCH', 'FIND'],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'The LIKE operator is used for pattern matching, often with wildcards like % and _.',
        category: 'pattern-matching',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'sql-basics-2-q3',
        question: 'What does the % wildcard represent in a LIKE clause?',
        options: [
          'Exactly one character',
          'Zero or more characters',
          'A percentage value',
          'A number'
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'The % wildcard represents zero or more characters in pattern matching.',
        category: 'wildcards',
        points: 15,
        type: 'multiple-choice'
      }
    ],
    'sql-basics-3': [
      {
        id: 'sql-basics-3-q1',
        question: 'What is the purpose of the ORDER BY clause?',
        options: [
          'To filter records',
          'To sort the result set',
          'To group records',
          'To join tables'
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'ORDER BY is used to sort the result set in ascending (ASC) or descending (DESC) order.',
        category: 'sorting',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'sql-basics-3-q2',
        question: 'Which keyword sorts data in descending order?',
        options: ['ASC', 'DESC', 'DOWN', 'REVERSE'],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: 'DESC sorts data in descending order (Z to A, 9 to 1).',
        category: 'sorting',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'sql-basics-3-q3',
        question: 'Can you sort by multiple columns in SQL?',
        options: [
          'No, only one column allowed',
          'Yes, using comma-separated column names',
          'Only with special functions',
          'Only in advanced databases'
        ],
        correctAnswer: 1,
        difficulty: 'medium',
        explanation: 'You can sort by multiple columns by listing them separated by commas in the ORDER BY clause.',
        category: 'multi-column-sorting',
        points: 15,
        type: 'multiple-choice'
      }
    ],
    'sql-intermediate-1': [
      {
        id: 'sql-intermediate-1-q1',
        question: 'What is the purpose of the GROUP BY clause?',
        options: [
          'To sort data',
          'To filter data',
          'To group rows with same values',
          'To join tables'
        ],
        correctAnswer: 2,
        difficulty: 'medium',
        explanation: 'GROUP BY groups rows that have the same values in specified columns.',
        category: 'grouping',
        points: 15,
        type: 'multiple-choice'
      },
      {
        id: 'sql-intermediate-1-q2',
        question: 'Which function counts the number of rows in a group?',
        options: ['SUM()', 'COUNT()', 'AVG()', 'MAX()'],
        correctAnswer: 1,
        difficulty: 'easy',
        explanation: 'COUNT() function returns the number of rows in a group or table.',
        category: 'aggregate-functions',
        points: 10,
        type: 'multiple-choice'
      },
      {
        id: 'sql-intermediate-1-q3',
        question: 'What is the difference between WHERE and HAVING clauses?',
        options: [
          'No difference',
          'WHERE filters rows, HAVING filters groups',
          'HAVING filters rows, WHERE filters groups',
          'They work with different databases'
        ],
        correctAnswer: 1,
        difficulty: 'hard',
        explanation: 'WHERE filters individual rows before grouping, HAVING filters groups after GROUP BY.',
        category: 'filtering-vs-grouping',
        points: 20,
        type: 'multiple-choice'
      }
    ]
  };

  // Return specific questions if available, otherwise generate default ones
  return questionSets[moduleId] || generateDefaultSQLQuestions(moduleId, moduleTitle);
}

function generateDefaultSQLQuestions(moduleId: string, moduleTitle: string): UnifiedQuizQuestion[] {
  return [
    {
      id: `${moduleId}-default-q1`,
      question: `What is the main focus of ${moduleTitle}?`,
      options: [
        'Database design principles',
        'Query optimization techniques', 
        'Data manipulation and retrieval',
        'All of the above'
      ],
      correctAnswer: 3,
      difficulty: 'medium',
      explanation: `${moduleTitle} covers multiple aspects of SQL including data manipulation, retrieval, and best practices.`,
      category: 'overview',
      points: 15,
      type: 'multiple-choice'
    },
    {
      id: `${moduleId}-default-q2`,
      question: `Which concept is most important to understand in ${moduleTitle}?`,
      options: [
        'Memorizing syntax',
        'Understanding data relationships',
        'Learning keyboard shortcuts',
        'Database administration'
      ],
      correctAnswer: 1,
      difficulty: 'medium',
      explanation: 'Understanding how data relates and can be queried is fundamental to SQL mastery.',
      category: 'concepts',
      points: 15,
      type: 'multiple-choice'
    },
    {
      id: `${moduleId}-default-q3`,
      question: `How should you practice the concepts from ${moduleTitle}?`,
      options: [
        'Only read documentation',
        'Write and execute SQL queries',
        'Watch videos without practicing',
        'Memorize examples'
      ],
      correctAnswer: 1,
      difficulty: 'easy',
      explanation: 'Hands-on practice by writing and executing SQL queries is the best way to learn.',
      category: 'practice',
      points: 10,
      type: 'multiple-choice'
    }
  ];
}

export const enhanceAllSQLModulesWithQuizzes = (modules: SQLModule[]): SQLModule[] => {
  return modules.map(module => {
    const enhanced = { ...module };

    // Convert UnifiedQuizQuestion to SQLQuizQuestion format
    const unifiedQuestions = createComprehensiveSQLQuestions(module.id, module.title);
    const sqlQuestions: SQLQuizQuestion[] = unifiedQuestions.map(q => ({
      id: q.id,
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswer,
      correctAnswerIndex: q.correctAnswer,
      difficulty: q.difficulty,
      explanation: q.explanation,
      category: q.category,
      points: q.points,
      // Ensure type compatibility - only use allowed types
      type: q.type === 'fill-in-blank' || q.type === 'coding' ? 'multiple-choice' : q.type
    }));

    enhanced.quiz = {
      questions: sqlQuestions,
      passThreshold: 75
    };

    // Ensure practice content exists
    if (!enhanced.sqlPractice && !enhanced.practice) {
      enhanced.sqlPractice = createDefaultSQLPractice(module.id, module.title);
    }

    // Ensure examples exist
    if (!enhanced.sqlExamples || enhanced.sqlExamples.length === 0) {
      enhanced.sqlExamples = createDefaultSQLExamples(module.id, module.title);
    }

    return enhanced;
  });
};

const createDefaultSQLPractice = (moduleId: string, moduleTitle: string) => {
  const practiceTemplates: Record<string, any> = {
    'sql-basics-1': {
      task: 'Write basic SELECT queries to retrieve data from a users table. Practice selecting all columns and specific columns.',
      expectedQuery: `-- Select all users
SELECT * FROM users;

-- Select specific columns
SELECT name, email FROM users;`,
      hints: [
        'Use SELECT * to get all columns',
        'List specific column names after SELECT for targeted data',
        'Remember to end statements with semicolon'
      ],
      initialSchema: `CREATE TABLE users (
  id INT PRIMARY KEY,
  name VARCHAR(100),
  email VARCHAR(100),
  created_at TIMESTAMP
);

INSERT INTO users VALUES 
(1, 'John Doe', 'john@example.com', '2024-01-01'),
(2, 'Jane Smith', 'jane@example.com', '2024-01-02');`
    },
    'sql-basics-2': {
      task: 'Practice using WHERE clauses to filter data. Write queries to find specific users based on different conditions.',
      expectedQuery: `-- Find users created after a specific date
SELECT * FROM users 
WHERE created_at > '2024-01-01';

-- Find users by name
SELECT * FROM users 
WHERE name = 'John Doe';`,
      hints: [
        'Use WHERE clause after FROM to filter results',
        'Use comparison operators like =, >, <, >=, <=',
        'Use quotes around text values'
      ]
    },
    'sql-basics-3': {
      task: 'Learn to sort and organize query results using ORDER BY. Practice sorting by different columns and directions.',
      expectedQuery: `-- Sort users by name alphabetically
SELECT * FROM users 
ORDER BY name ASC;

-- Sort by creation date, newest first
SELECT * FROM users 
ORDER BY created_at DESC;`,
      hints: [
        'Use ORDER BY at the end of your query',
        'ASC for ascending (A-Z, 1-9), DESC for descending (Z-A, 9-1)',
        'You can sort by multiple columns'
      ]
    }
  };

  return practiceTemplates[moduleId] || {
    task: `Practice ${moduleTitle} concepts by writing SQL queries that demonstrate the key learning objectives.`,
    expectedQuery: `-- Example query for ${moduleTitle}
SELECT column1, column2 
FROM table_name 
WHERE condition = 'value'
ORDER BY column1;`,
    hints: [
      'Start with basic SELECT statement structure',
      'Add WHERE clause for filtering if needed',
      'Use ORDER BY to sort results',
      'Test your query step by step'
    ],
    initialSchema: `-- Database schema for ${moduleTitle}
CREATE TABLE sample_table (
  id INT PRIMARY KEY,
  name VARCHAR(100),
  category VARCHAR(50),
  created_at TIMESTAMP
);`
  };
};

const createDefaultSQLExamples = (moduleId: string, moduleTitle: string) => {
  return [
    {
      query: 'SELECT * FROM users LIMIT 5;',
      explanation: `Basic query to retrieve first 5 records from users table`,
      result: {
        columns: ['id', 'name', 'email', 'created_at'],
        rows: [
          [1, 'John Doe', 'john@example.com', '2024-01-01'],
          [2, 'Jane Smith', 'jane@example.com', '2024-01-02'],
          [3, 'Bob Johnson', 'bob@example.com', '2024-01-03']
        ]
      }
    },
    {
      query: `SELECT name, email FROM users WHERE created_at >= '2024-01-01';`,
      explanation: `Filtered query showing specific columns with date condition`,
      result: {
        columns: ['name', 'email'],
        rows: [
          ['John Doe', 'john@example.com'],
          ['Jane Smith', 'jane@example.com']
        ]
      }
    }
  ];
};
